import { Title, Toast, ValidationIcon } from '@yoast/ui-library';
import classNames from 'classnames';
import React, { useContext } from 'react';
import ReactDOM from 'react-dom';

import { ToastContext } from '../contexts/ToastContext';

/**
 * Container component that renders all active toasts.
 * This component uses a portal to render toasts in a fixed position at the bottom-left.
 * Uses @yoast/ui-library Toast component for styling and behavior.
 */
export const ToastContainer: React.FC = () => {
  const context = useContext(ToastContext);

  if (!context) {
    return null;
  }

  const { toasts, removeToast } = context;

  if (typeof window === 'undefined') {
    return null;
  }

  // Render into the hook-toasts container which is a child of the main toast-portal
  // The parent container handles positioning, we just stack our toasts vertically
  const toastRoot = document.getElementById('hook-toasts');

  if (!toastRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={classNames(
        // Parent container handles positioning, we just stack vertically
        'yst-w-full yst-space-y-4',
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <Toast
          className={classNames('yst-w-full', toast.className)}
          key={toast.id}
          id={toast.id}
          variant={toast.variant || 'info'}
          autoDismiss={toast.dismissible ? 0 : (toast.duration ?? 1500)}
          onDismiss={() => removeToast(toast.id)}
          isVisible
          setIsVisible={() => {}}
        >
          {/* Toast Body */}
          <div className="yst-flex yst-gap-3">
            <div className="yst-flex-shrink-0">
              <ValidationIcon
                className="yst-h-5 yst-w-5"
                variant={toast.variant}
              />
            </div>
            <div className="yst-flex-1">
              {typeof toast.title === 'string' ? (
                <Title title={toast.title} />
              ) : (
                toast.title
              )}
              {typeof toast.message === 'string' ? (
                <p className="yst-mb-0">{toast.message}</p>
              ) : (
                toast.message
              )}
            </div>

            {toast.dismissible && (
              <div>
                <Toast.Close
                  className="yst-p-0 yst-text-slate-400"
                  dismissScreenReaderLabel="Dismiss"
                />
              </div>
            )}
          </div>

          {/* Toast Actions */}
          {/* allow user to pass in ReactNode for buttons UI */}
          {toast.actions && (
            <div className="yst-mt-3 yst-flex yst-justify-end yst-gap-3">
              <toast.actions dismiss={() => removeToast(toast.id)} />
            </div>
          )}
        </Toast>
      ))}
    </div>,
    toastRoot,
  );
};
